import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // IMPORTANTE: Envía cookies HTTP-only automáticamente
});

// Interceptor de respuesta para manejar errores de autenticación
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expirado o inválido - redirigir al login
      // Las cookies se manejan automáticamente por el navegador
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  me: () => api.get('/auth/me'),
  changePassword: (oldPassword, newPassword) => 
    api.post('/auth/change-password', { old_password: oldPassword, new_password: newPassword }),
};

export const locationsAPI = {
  getAll: (activeOnly = false) => api.get(`/locations?active_only=${activeOnly}`),
  getById: (id) => api.get(`/locations/${id}`),
  getMyInfo: () => api.get('/locations/my/info'),
  create: (data) => api.post('/locations', data),
  update: (id, data) => api.patch(`/locations/${id}`, data),
  delete: (id) => api.delete(`/locations/${id}`),
};

export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.patch(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  uploadProfileImage: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/users/${id}/profile-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteProfileImage: (id) => api.delete(`/users/${id}/profile-image`),
};

export const itemsAPI = {
  getAll: (activeOnly = true, category = null) => {
    let url = `/items?active_only=${activeOnly}`;
    if (category) url += `&category=${category}`;
    return api.get(url);
  },
  getById: (id) => api.get(`/items/${id}`),
  getLowStock: () => api.get('/items/low-stock'),
  getByMaestro: (maestro) => api.get(`/items/by-maestro/${encodeURIComponent(maestro)}`),
  create: (data) => api.post('/items', data),
  update: (id, data) => api.patch(`/items/${id}`, data),
  delete: (id) => api.delete(`/items/${id}`),
};

export const inventoryAPI = {
  adjust: (itemId, data) => api.post(`/inventory/adjust/${itemId}`, data),
  getMovements: (itemId = null, limit = 100) => {
    let url = `/inventory/movements?limit=${limit}`;
    if (itemId) url += `&item_id=${itemId}`;
    return api.get(url);
  },
  getHistory: (params = {}) => {
    const { warehouseId, movementType, startDate, endDate, limit = 100 } = params;
    let url = `/inventory/history?limit=${limit}`;
    if (warehouseId) url += `&warehouse_id=${warehouseId}`;
    if (movementType) url += `&movement_type=${movementType}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    return api.get(url);
  },
  syncStock: () => api.post('/inventory/sync-stock'),
  recalculateTotals: () => api.post('/inventory/recalculate-totals'),
};

export const ordersAPI = {
  create: (data) => api.post('/orders', data),
  submit: (orderId) => api.post(`/orders/${orderId}/submit`),
  getCurrent: (date, maestro = null) => {
    let url = `/orders/current?order_date=${date}`;
    if (maestro) url += `&maestro=${encodeURIComponent(maestro)}`;
    return api.get(url);
  },
  getCurrentAllMaestros: (date) => api.get(`/orders/current/all-maestros?order_date=${date}`),
  getHistory: (limit = 30) => api.get(`/orders/history?limit=${limit}`),
  getByDate: (date, warehouseId = null) => {
    let url = `/orders/by-date?order_date=${date}`;
    if (warehouseId) url += `&warehouse_id=${warehouseId}`;
    return api.get(url);
  },
  getConsolidated: (date, warehouseId = null, orderType = null) => {
    let url = `/orders/consolidated?order_date=${date}`;
    if (warehouseId) url += `&warehouse_id=${warehouseId}`;
    if (orderType) url += `&order_type=${orderType}`;
    return api.get(url);
  },
  getTopProducts: (limit = 10) => api.get(`/orders/stats/top-products?limit=${limit}`),
  getTopByLocation: (limit = 10) => api.get(`/orders/stats/top-by-location?limit=${limit}`),
  getByLocationPeriod: (period = 'week') => api.get(`/orders/stats/by-location-period?period=${period}`),
  getProductsStatsByPeriod: (period = 'week', startDate = null, endDate = null, limit = 20) => {
    let url = `/orders/stats/products-by-period?period=${period}&limit=${limit}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    return api.get(url);
  },
  getOrderTrends: (days = 30) => api.get(`/orders/stats/trends?days=${days}`),
  debugOrphanOrders: (date = null) => {
    let url = '/orders/debug/orphan-orders';
    if (date) url += `?order_date=${date}`;
    return api.get(url);
  },
  debugProductOrders: (itemName, startDate, endDate) => {
    return api.get(`/orders/debug/product-orders?item_name=${encodeURIComponent(itemName)}&start_date=${startDate}&end_date=${endDate}`);
  },
  getSummaryByPeriod: (period = '7days', startDate = null, endDate = null) => {
    let url = `/orders/stats/summary-by-period?period=${period}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    return api.get(url);
  },
  getSuggestedOrder: () => api.get('/orders/suggested-order'),
  getLocationsStatus: (date, warehouseId = null) => {
    let url = `/orders/locations-status?order_date=${date}`;
    if (warehouseId) url += `&warehouse_id=${warehouseId}`;
    return api.get(url);
  },
  getPreviousDeclaredStock: () => api.get('/orders/previous-declared-stock'),
};

export const dispatchAPI = {
  getPending: (date) => api.get(`/dispatch/pending?order_date=${date}`),
  create: (data) => api.post('/dispatch', data),
  getByDate: (date) => api.get(`/dispatch/by-date?dispatch_date=${date}`),
  getByOrder: (orderId) => api.get(`/dispatch/order/${orderId}`),
  update: (dispatchId, data) => api.put(`/dispatch/${dispatchId}`, data),
  get: (dispatchId) => api.get(`/dispatch/${dispatchId}`),
  getFulfillmentStats: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('start_date', params.startDate);
    if (params.endDate) queryParams.append('end_date', params.endDate);
    if (params.productId) queryParams.append('product_id', params.productId);
    if (params.supplier) queryParams.append('supplier', params.supplier);
    return api.get(`/dispatch/stats/fulfillment?${queryParams.toString()}`);
  }
};

export const routesAPI = {
  getPendingByLocation: (date) => api.get(`/routes/pending-by-location?order_date=${date}`),
  create: (data) => api.post('/routes', data),
  getByDate: (date) => api.get(`/routes/by-date?route_date=${date}`),
  getHistory: (limit = 50) => api.get(`/routes/history?limit=${limit}`),
  get: (routeId) => api.get(`/routes/${routeId}`),
};

export const importsAPI = {
  downloadTemplate: () => api.get('/imports/template', { responseType: 'blob' }),
  preview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/imports/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  execute: (file, mode = 'create') => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/imports/execute?mode=${mode}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const maestrosAPI = {
  getAll: (activeOnly = true) => api.get(`/maestros?active_only=${activeOnly}`),
  getById: (id) => api.get(`/maestros/${id}`),
  create: (data) => api.post('/maestros', data),
  update: (id, data) => api.patch(`/maestros/${id}`, data),
  delete: (id) => api.delete(`/maestros/${id}`),
};

export const logisticsStatsAPI = {
  getSummary: (startDate, endDate) => {
    let url = '/logistics-stats/summary';
    const params = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (params.length) url += '?' + params.join('&');
    return api.get(url);
  },
  getByLocation: (startDate, endDate) => {
    let url = '/logistics-stats/by-location';
    const params = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (params.length) url += '?' + params.join('&');
    return api.get(url);
  },
  getTopShortages: (startDate, endDate, limit = 10) => {
    let url = `/logistics-stats/top-shortages?limit=${limit}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    return api.get(url);
  },
  getRecentRoutes: (limit = 20) => api.get(`/logistics-stats/recent-routes?limit=${limit}`),
};

export const siteSettingsAPI = {
  get: () => api.get('/settings'),
  uploadLogoHorizontal: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/settings/logo/horizontal', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadLogoSquare: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/settings/logo/square', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadLogoLogin: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/settings/logo/login', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  resetLogo: (type) => api.post(`/settings/logo/reset/${type}`),
  update: (data) => api.patch('/settings', data),
};

// API de Recepciones de Proveedor
export const receptionsAPI = {
  create: (data) => api.post('/receptions', data),
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.supplier) queryParams.append('supplier', params.supplier);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.limit) queryParams.append('limit', params.limit);
    const query = queryParams.toString();
    return api.get(`/receptions${query ? '?' + query : ''}`);
  },
  getById: (id) => api.get(`/receptions/${id}`),
  update: (id, data) => api.patch(`/receptions/${id}`, data),
  getSuppliers: () => api.get('/receptions/suppliers/list'),
  getItemsBySupplier: (supplier) => api.get(`/receptions/items/by-supplier/${encodeURIComponent(supplier)}`),
};

// API de Bodegas
export const warehousesAPI = {
  getAll: () => api.get('/warehouses'),
  getActive: () => api.get('/warehouses/active'),
  getById: (id) => api.get(`/warehouses/${id}`),
  create: (data) => api.post('/warehouses', data),
  update: (id, data) => api.patch(`/warehouses/${id}`, data),
};

// API de Precios
export const pricesAPI = {
  getAll: (supplier = null) => {
    const query = supplier ? `?supplier=${encodeURIComponent(supplier)}` : '';
    return api.get(`/prices${query}`);
  },
  getAnalytics: () => api.get('/prices/analytics'),
  getByItem: (itemId) => api.get(`/prices/${itemId}`),
  addPrice: (data) => api.post('/prices', data),
  updatePrice: (entryId, data) => api.put(`/prices/entry/${entryId}`, data),
  deletePrice: (entryId) => api.delete(`/prices/entry/${entryId}`),
  getSuppliers: () => api.get('/prices/suppliers/list'),
  getAllSuppliers: () => api.get('/prices/suppliers/all'),
  // Comparación de precios por proveedor
  getComparison: () => api.get('/prices/compare/by-product'),
  addSupplierPrice: (itemId, supplier, price, quoteDate = null) => {
    let url = `/prices/supplier-price?item_id=${itemId}&supplier=${encodeURIComponent(supplier)}&price=${price}`;
    if (quoteDate) url += `&quote_date=${quoteDate}`;
    return api.post(url);
  },
};

// API de Proveedores
export const suppliersAPI = {
  getAll: (activeOnly = false) => api.get(`/suppliers?active_only=${activeOnly}`),
  getNames: () => api.get('/suppliers/names'),
  getById: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
  syncFromProducts: () => api.post('/suppliers/sync-from-products'),
};

// API de Inventario de Local (Inventarista)
export const localInventoryAPI = {
  getMyLocation: () => api.get('/local-inventory/my-location'),
  getItems: (subWarehouseId = null) => {
    const query = subWarehouseId ? `?sub_warehouse_id=${subWarehouseId}` : '';
    return api.get(`/local-inventory/items${query}`);
  },
  create: (data) => api.post('/local-inventory/', data),
  getHistory: (params = {}) => {
    const query = new URLSearchParams();
    if (params.locationId) query.append('location_id', params.locationId);
    if (params.warehouseId) query.append('warehouse_id', params.warehouseId);
    if (params.startDate) query.append('start_date', params.startDate);
    if (params.endDate) query.append('end_date', params.endDate);
    if (params.limit) query.append('limit', params.limit);
    return api.get(`/local-inventory/history?${query.toString()}`);
  },
  getById: (id) => api.get(`/local-inventory/${id}`),
  update: (id, data) => api.put(`/local-inventory/${id}`, data),
  delete: (id) => api.delete(`/local-inventory/${id}`),
  getStats: (locationId = null) => {
    const query = locationId ? `?location_id=${locationId}` : '';
    return api.get(`/local-inventory/stats/summary${query}`);
  },
  // Para que el admin vea todos los inventarios de los inventaristas
  getAllForAdmin: (params = {}) => {
    const query = new URLSearchParams();
    if (params.warehouse_id) query.append('warehouse_id', params.warehouse_id);
    if (params.location_id) query.append('location_id', params.location_id);
    if (params.start_date) query.append('start_date', params.start_date);
    if (params.end_date) query.append('end_date', params.end_date);
    if (params.limit) query.append('limit', params.limit);
    return api.get(`/local-inventory/admin/all?${query.toString()}`);
  },
  // Aplicar un inventario específico al stock
  applyToStock: (inventoryId) => api.post(`/local-inventory/admin/apply-inventory/${inventoryId}`),
  // Aplicar todos los inventarios pendientes
  applyAllPending: (params = {}) => {
    const query = new URLSearchParams();
    if (params.warehouse_id) query.append('warehouse_id', params.warehouse_id);
    return api.post(`/local-inventory/admin/apply-all-pending?${query.toString()}`);
  },
  // Diagnóstico de locales y bodegas
  debugLocationsWarehouses: () => api.get('/local-inventory/debug/locations-warehouses'),
  // Asignar bodega a un local
  assignWarehouseToLocation: (locationId, warehouseId) => 
    api.post(`/local-inventory/admin/assign-warehouse-to-location?location_id=${locationId}&warehouse_id=${warehouseId}`),
};

// API de Sub-Bodegas
export const subWarehousesAPI = {
  getAll: (parentWarehouseId = null, activeOnly = false) => {
    const params = new URLSearchParams();
    if (parentWarehouseId) params.append('parent_warehouse_id', parentWarehouseId);
    if (activeOnly) params.append('active_only', 'true');
    return api.get(`/sub-warehouses/?${params.toString()}`);
  },
  getByWarehouse: (warehouseId) => api.get(`/sub-warehouses/by-warehouse/${warehouseId}`),
  getById: (id) => api.get(`/sub-warehouses/${id}`),
  create: (data) => api.post('/sub-warehouses/', data),
  update: (id, data) => api.patch(`/sub-warehouses/${id}`, data),
  delete: (id) => api.delete(`/sub-warehouses/${id}`),
};

// API de Envíos a Locales
export const shipmentsAPI = {
  create: (data) => api.post('/shipments/', data),
  getAll: (params = {}) => {
    const query = new URLSearchParams();
    if (params.warehouse_id) query.append('warehouse_id', params.warehouse_id);
    if (params.location_id) query.append('location_id', params.location_id);
    if (params.start_date) query.append('start_date', params.start_date);
    if (params.end_date) query.append('end_date', params.end_date);
    if (params.limit) query.append('limit', params.limit);
    return api.get(`/shipments/?${query.toString()}`);
  },
  getById: (id) => api.get(`/shipments/${id}`),
  getStats: (params = {}) => {
    const query = new URLSearchParams();
    if (params.warehouse_id) query.append('warehouse_id', params.warehouse_id);
    if (params.days) query.append('days', params.days);
    return api.get(`/shipments/stats?${query.toString()}`);
  },
  getToday: () => api.get('/shipments/today'),
  cancel: (id) => api.delete(`/shipments/${id}`),
};

export default api;